import React from 'react';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import styled from 'styled-components';
import {SearchField} from '../inputs/search/SearchField';
import {CheckboxMenuItem} from './customDropdown/CheckboxMenuItem';
import {CustomDropdown} from './customDropdown/CustomDropdown';
import {MobileFilterHeading} from './MobileFilterHeading';
import {FilterOption, SelectedFilters} from './FiltersBar';

const Styled = styled.div<{accentColor: AccentColorOptions; noBottomSpacing?: boolean}>`
    background-color: ${(p) =>
        p.theme.ifLight(p, p.theme.palette.common.white, p.theme.palette.background.paper)};
    display: inline-block;
    width: 100%;
    margin: 20px 0 20px 0;
    font-size: 16px;

    .select-dropdown {
        /* sit on top of matui-elevation 1 cards */
        z-index: 101;
        max-height: 400px;
        min-width: 260px;
        overflow-y: auto;
        scrollbar-width: thin;
        &::-webkit-scrollbar {
            width: 8px;
        }
        &::-webkit-scrollbar-track {
            background-color: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.neutral.pearl, p.theme.palette.neutral.steel)};
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${(p) => p.theme.palette.neutral.silver};
            border-radius: 10px;
        }
    }

    .totals-count {
        border-right: 1px solid ${(p) => p.theme.palette.background.paperpop};
        padding: 25px;
        display: inline-block;
        min-width: 80px;
        text-align: center;
        margin-right: 10px;
    }

    .search {
        display: inline;
        height: 100%;
        float: right;
        border-left: 1px solid #6c6c6e;
        padding: 11px;
        width: 250px;
    }

    .filter-group {
        display: inline-block;
        min-width: 100px;
        margin-right: 12px;
        && .select-label div.paragraph {
            font-weight: 500;
        }

        .select-arrow path {
            stroke: ${(p) => p.theme.palette.accent[p.accentColor]};
        }
    }

    .filters-bar {
        display: inline-block;
    }

    .search-field {
        div,
        input {
            background-color: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.common.white, p.theme.palette.background.paper)};
        }

        .ue-icon-search {
            height: 16px;
            color: ${(p) => p.theme.palette.accent[p.accentColor]};
        }
    }

    ${(p) => p.theme.breakpoints.down('sm')} {
        position: fixed;
        z-index: 99999;
        left: 0;
        width: 100%;
        top: 0;
        height: 100vh;
        background-color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.common.white, p.theme.palette.background.paper)};
        display: none;

        &&.opened-mobile {
            display: block;
            height: 100vh;
            max-height: -webkit-fill-available;
            overflow-y: scroll;
            -webkit-overflow-scrolling: touch;

            margin: 0;
        }

        .filters-bar.opened-mobile {
            width: 100%;
            background-color: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.common.white, p.theme.palette.background.paper)};
            z-index: 2;

            height: auto;
            .filter-group {
                width: calc(100% - 60px);
                margin-left: 15px;
                margin-right: 15px;
            }
        }

        .selectCustom .select-dropdown {
            width: 100%;
            z-index: 1;
        }

        .search.opened-mobile,
        .totals-count.opened-mobile {
            display: none;
        }

        .selectCustom .select-label {
            float: unset;
            border-bottom: 1px solid ${(p) => p.theme.palette.neutral.silver};
        }
        .select-label {
            width: 100%;
        }
        .select-dropdown {
            position: unset;
            z-index: 2;
            margin: 6px 0;
        }
    }

    ${(p) => p.noBottomSpacing && `margin-bottom: 0px;`}
`;

export interface FiltersBarDesktopProps<T> {
    accentColor?: AccentColorOptions;
    resultsList?: T[];
    totalCount?: number;
    filterOptions?: FilterOption<T>[];
    filteredDataLabel?: string;
    filteredDataLabelPlural?: string;
    desktopSearchLabel?: string;
    onFilterSelect: ({
        search,
        selectedFilters
    }: {
        search?: string;
        selectedFilters?: {os; twinmotionVersions};
    }) => void;
    search?: string;
    setSearch: (value: string) => void;
    openedFilterBarMobile?: boolean;
    setOpenedFilterBarMobile: (b: boolean) => void;
    mobileFilterTitle?: string;
    selectedFilters?: SelectedFilters;
    updateSelectedFilters: (e, filterLabel, value, isChecked) => void;
    noBottomSpacing?: boolean;
    totalCountIsPreferredCount?: boolean;
}

export const FiltersBarDesktop = <T,>({
    accentColor = 'sky',
    resultsList,
    totalCount,
    totalCountIsPreferredCount,
    filterOptions,
    filteredDataLabel = 'item',
    filteredDataLabelPlural = 'items',
    desktopSearchLabel = 'Search',
    onFilterSelect,
    search,
    setSearch,
    openedFilterBarMobile,
    setOpenedFilterBarMobile,
    mobileFilterTitle,
    selectedFilters,
    updateSelectedFilters,
    noBottomSpacing
}: FiltersBarDesktopProps<T>): JSX.Element => {
    const isSm = useBreakpointDown('sm');

    //when totalCountIsPreferredCount is false (default), fallback on  resultsList?.length if totalCount is undefined
    const count = totalCountIsPreferredCount ? totalCount : totalCount || resultsList?.length;

    return (
        <Styled
            accentColor={accentColor}
            noBottomSpacing={noBottomSpacing}
            className={`${openedFilterBarMobile ? 'opened-mobile' : ''} `}>
            <div className={`totals-count ${openedFilterBarMobile ? 'opened-mobile' : ''} `}>
                {count !== undefined && <>{count} {count === 1 ? filteredDataLabel : filteredDataLabelPlural}{' '}</>}
            </div>
            <div className={`filters-bar ${openedFilterBarMobile ? 'opened-mobile' : ''} `}>
                {isSm && (
                    <MobileFilterHeading
                        setOpenedFilterBarMobile={setOpenedFilterBarMobile}
                        filterLabel={mobileFilterTitle}
                    />
                )}

                {filterOptions?.map((filterOption, i) => {
                    return (
                        <div className="filter-group" key={i}>
                            <CustomDropdown label={filterOption.label} hasCheckedItems={false}>
                                {filterOption?.checkboxOptions?.map((checkboxItem, j) => {
                                    const options = selectedFilters?.[filterOption.name] || [];
                                    const regex = new RegExp(options.join('|'), 'i');
                                    const checked =
                                        (options.length && regex.test(checkboxItem)) || false;
                                    const disabled = false;

                                    return (
                                        <CheckboxMenuItem
                                            key={`${i}${j}`}
                                            accentColor={accentColor}
                                            sharp
                                            checkboxProps={{
                                                checked,
                                                disabled
                                            }}
                                            onClick={(e) => {
                                                updateSelectedFilters(
                                                    e,
                                                    filterOption.name,
                                                    checkboxItem,
                                                    checked
                                                );
                                                if (typeof filterOption.onClick === 'function') {
                                                    filterOption.onClick(e, checkboxItem, !checked);
                                                }
                                            }}>
                                            {checkboxItem}
                                        </CheckboxMenuItem>
                                    );
                                })}
                            </CustomDropdown>
                        </div>
                    );
                })}
            </div>

            <div className={`search ${openedFilterBarMobile ? 'opened-mobile' : ''} `}>
                <SearchField
                    onChange={(searchText) => {
                        setSearch(searchText);
                        onFilterSelect({search: searchText});
                    }}
                    searchLabelText={desktopSearchLabel}
                    searchKeyword={search}
                />
            </div>
        </Styled>
    );
};
